import { inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthApiService } from '../../services/auth-api.service';
import { AuthState, UserDTO, CompanyContextDTO } from '../../types/auth.types';
import {
  matchAuditRoutePermission,
  matchExpertRoutePermission,
} from '../route-permissions';
import { ServicePermissionUtils } from '../../utils/service-permission.utils';
import { SERVICE_KEY } from '../definitions';

const COMPANY_COOKIE_KEY = 'current_company';
const COMPANY_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

const persistCompanyCookie = (company: CompanyContextDTO | null) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (company) {
    document.cookie = `${COMPANY_COOKIE_KEY}=${encodeURIComponent(
      JSON.stringify(company)
    )};path=/;max-age=${COMPANY_COOKIE_MAX_AGE};samesite=lax`;
  } else {
    document.cookie = `${COMPANY_COOKIE_KEY}=;path=/;max-age=0;samesite=lax`;
  }
};

const readCompanyCookie = (): CompanyContextDTO | null => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COMPANY_COOKIE_KEY}=`));

  if (!cookie) {
    return null;
  }

  const value = decodeURIComponent(cookie.split('=')[1] ?? '');

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as CompanyContextDTO;
  } catch (error) {
    console.error('Failed to parse company context cookie:', error);
    document.cookie = `${COMPANY_COOKIE_KEY}=;path=/;max-age=0;samesite=lax`;
    return null;
  }
};

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  tokens: {
    access_token: null,
    refresh_token: null,
    expires_in: null,
  },
  sessionId: null,
  currentCompany: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    isLoggedIn: computed(() => {
      return !!store.user() && !!store.tokens().access_token;
    }),
    userFullName: computed(() => {
      const user = store.user();
      return user ? `${user.first_name} ${user.last_name}` : '';
    }),
    isTokenExpired: computed(() => {
      const expiresIn = store.tokens().expires_in;
      if (!expiresIn) return true;
      return Date.now() >= expiresIn * 1000;
    }),
    expertPermissions: computed(() => {
      const user = store.user();
      return user?.svc_perms?.[SERVICE_KEY.EXPERT] ?? [];
    }),
    expertServiceRoles: computed(() => {
      const user = store.user();
      return user?.svc_roles?.[SERVICE_KEY.EXPERT] ?? [];
    }),
    isCurrentUserExpert: computed(() => {
      const user = store.user();
      return user?.is_expert === true && !!user?.expert_id;
    }),
    currentUserExpertId: computed(() => {
      const user = store.user();
      return user?.expert_id ?? null;
    }),
    hasAdminRole: computed(() => {
      const user = store.user();
      return user?.roles?.includes('admin');
    }),
    hasSupportRole: computed(() => {
      const user = store.user();
      return user?.roles?.includes('support');
    }),
    hasAdminOrSupportRole: computed(() => {
      const user = store.user();
      return user?.roles?.includes('admin') || user?.roles?.includes('support');
    }),
  })),

  withMethods((store) => {
    const authApiService = inject(AuthApiService);

    return {
      setLoading: (loading: boolean) => {
        patchState(store, { isLoading: loading });
      },

      setError: (error: string | null) => {
        patchState(store, { error });
      },

      clearError: () => {
        patchState(store, { error: null });
      },

      /**
       * Load auth data from localStorage
       */
      loadFromStorage: () => {
        const accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        const expiresIn = localStorage.getItem('expires_in');
        const userJson = localStorage.getItem('user');
        const sessionId = localStorage.getItem('session_id');
        const currentCompanyJson = localStorage.getItem('current_company');

        if (accessToken && refreshToken && expiresIn && userJson) {
          try {
            const user: UserDTO = JSON.parse(userJson);
            patchState(store, {
              user,
              isAuthenticated: true,
              tokens: {
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_in: parseInt(expiresIn, 10),
              },
              sessionId: sessionId,
            });

            // Load company context
            if (currentCompanyJson) {
              try {
                const currentCompany: CompanyContextDTO =
                  JSON.parse(currentCompanyJson);
                patchState(store, { currentCompany });
                persistCompanyCookie(currentCompany);
              } catch (error) {
                console.error('Error parsing current company:', error);
                localStorage.removeItem('current_company');
                persistCompanyCookie(null);
              }
            }

            if (!store.currentCompany()) {
              const companyFromCookie = readCompanyCookie();
              if (companyFromCookie) {
                patchState(store, { currentCompany: companyFromCookie });
                localStorage.setItem(
                  'current_company',
                  JSON.stringify(companyFromCookie)
                );
              }
            }
          } catch (error) {
            console.error('Error parsing stored user data:', error);
            // Clear auth data by resetting to initial state
            patchState(store, initialState);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('expires_in');
            localStorage.removeItem('user');
            localStorage.removeItem('session_id');
            localStorage.removeItem('current_company');
            persistCompanyCookie(null);
          }
        }
      },

      /**
       * Logout a user
       * @returns Promise<void>
       */
      logout: async (): Promise<void> => {
        patchState(store, { isLoading: true });

        try {
          const refreshToken = store.tokens().refresh_token;
          const sessionId = store.sessionId();

          if (refreshToken) {
            await firstValueFrom(
              authApiService.logout({
                refresh_token: refreshToken,
                session_id: sessionId || undefined,
              })
            );
          }
        } catch (error) {
          // Even if logout fails on server, we still clear local state
          console.error('Logout error:', error);
        } finally {
          // Clear auth data
          patchState(store, initialState);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('expires_in');
          localStorage.removeItem('user');
          localStorage.removeItem('session_id');
          localStorage.removeItem('current_company');
          persistCompanyCookie(null);
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Update a user's profile
       * @param user - UserDTO
       */
      updateUser: (user: UserDTO) => {
        patchState(store, { user });
        // Update localStorage with the new user data
        localStorage.setItem('user', JSON.stringify(user));
      },

      hasExpertPermission: (permission: string | string[]): boolean => {
        const permissions = Array.isArray(permission)
          ? permission
          : [permission];

        if (permissions.length === 0) {
          return true;
        }

        return ServicePermissionUtils.hasAllPermissions(
          store.user(),
          SERVICE_KEY.EXPERT,
          permissions
        );
      },

      hasAnyExpertPermission: (permissions: string[]): boolean => {
        if (!permissions.length) {
          return true;
        }

        return ServicePermissionUtils.hasAnyPermission(
          store.user(),
          SERVICE_KEY.EXPERT,
          permissions
        );
      },

      canAccessExpertRoute: (route: string): boolean => {
        const config = matchExpertRoutePermission(route);

        if (!config || config.requiredPermissions.length === 0) {
          return true;
        }

        return config.requireAll === false
          ? ServicePermissionUtils.hasAnyPermission(
              store.user(),
              SERVICE_KEY.EXPERT,
              config.requiredPermissions
            )
          : ServicePermissionUtils.hasAllPermissions(
              store.user(),
              SERVICE_KEY.EXPERT,
              config.requiredPermissions
            );
      },

      hasAuditPermission: (permission: string | string[]): boolean => {
        const permissions = Array.isArray(permission)
          ? permission
          : [permission];

        if (permissions.length === 0) {
          return true;
        }

        return ServicePermissionUtils.hasAllPermissions(
          store.user(),
          SERVICE_KEY.AUDIT,
          permissions
        );
      },

      hasAnyAuditPermission: (permissions: string[]): boolean => {
        if (!permissions.length) {
          return true;
        }

        return ServicePermissionUtils.hasAnyPermission(
          store.user(),
          SERVICE_KEY.AUDIT,
          permissions
        );
      },

      canAccessAuditRoute: (route: string): boolean => {
        const config = matchAuditRoutePermission(route);

        if (!config || config.requiredPermissions.length === 0) {
          return true;
        }

        return config.requireAll === false
          ? ServicePermissionUtils.hasAnyPermission(
              store.user(),
              SERVICE_KEY.AUDIT,
              config.requiredPermissions
            )
          : ServicePermissionUtils.hasAllPermissions(
              store.user(),
              SERVICE_KEY.AUDIT,
              config.requiredPermissions
            );
      },
    };
  })
);

// Create a type for the store instance
export type AuthStoreType = InstanceType<typeof AuthStore>;
